"use client";

import { useState } from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Toggle } from "@/components/ui/toggle";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheInvestment() {
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const plans = [{
    name: "Editorial Session",
    tagline: "Perfect for high-fashion shoots, headshots, or creative portfolio updates.",
    priceMonthly: 250,
    priceAnnual: 212,
    featured: false,
    features: ["Professional skin prep & priming", "Full editorial makeup application", "On-set touch-up assistance", "Custom lash application", "High-definition finish"],
    cta: "Book Session"
  }, {
    name: "Bridal Signature",
    tagline: "The ultimate luxury experience for your wedding day glow.",
    priceMonthly: 450,
    priceAnnual: 382,
    featured: true,
    features: ["Comprehensive bridal consultation", "Full-day on-site artistry", "Bridal party touch-up kit", "Airbrush or traditional foundation", "Priority scheduling & coordination"],
    cta: "Reserve Date"
  }, {
    name: "Artistry Lessons",
    tagline: "Master your own glow with personalized one-on-one technique training.",
    priceMonthly: 150,
    priceAnnual: 127,
    featured: false,
    features: ["Personalized product audit", "Step-by-step technique training", "Customized color matching", "Professional tool guidance", "Digital guide & cheat sheet"],
    cta: "Book Lesson"
  }];
  return <section data-section-id="247" id="the-investment" className="dark bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-6">INVESTMENT</p>
            <h2 className="font-serif font-default text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Curated artistry.{" "}
              <span className="italic text-primary">Tailored glow.</span>
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground">
              Every service is designed to enhance your natural beauty with precision and care. Choose the experience that fits your vision — from editorial precision to bridal elegance.
            </p>

            <div className="flex items-center gap-4 flex-wrap">
              <div role="group" aria-label="Billing period" className="inline-flex items-center gap-1 rounded-lg border border-border bg-card p-1">
                <Toggle pressed={billing === "monthly"} onPressedChange={() => setBilling("monthly")} size="sm" className="font-mono text-xs uppercase tracking-wider data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-4" aria-label="Standard pricing">
                  Standard
                </Toggle>
                <Toggle pressed={billing === "annual"} onPressedChange={() => setBilling("annual")} size="sm" className="font-mono text-xs uppercase tracking-wider data-[state=on]:bg-primary data-[state=on]:text-primary-foreground px-4" aria-label="Package pricing">
                  Package
                </Toggle>
              </div>
              <Badge className="bg-[var(--chart-2)] text-primary-foreground font-mono text-[0.65rem] uppercase tracking-widest px-2.5 py-1 rounded-md border-transparent">
                Save 15%
              </Badge>
            </div>
          </div>
        </div>

        {}
        <div ref={cardsRef} className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch transition-all duration-700 delay-150 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {plans.map((plan, i) => {
          const price = billing === "monthly" ? plan.priceMonthly : plan.priceAnnual;
          const isFeatured = plan.featured;
          return <div key={plan.name} data-index={i} className={`relative flex ${isFeatured ? "lg:-translate-y-4" : ""} transition-transform duration-300 hover:-translate-y-2`}>
                {isFeatured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-[var(--chart-2)] text-primary-foreground font-mono text-[0.65rem] uppercase tracking-widest px-3 py-1.5 rounded-md border-transparent shadow-md">
                      <span className="mr-1.5">●</span> Signature
                    </Badge>
                  </div>}

                <Card className={`w-full flex flex-col p-8 md:p-10 rounded-lg transition-shadow duration-300 ${isFeatured ? "bg-primary text-primary-foreground border-transparent shadow-xl hover:shadow-2xl" : "bg-card text-card-foreground border border-border shadow-sm hover:shadow-lg"}`}>
                  {}
                  <h3 className={`font-serif font-default text-2xl font-normal ${isFeatured ? "text-primary-foreground" : "text-foreground"}`}>
                    {plan.name}
                  </h3>

                  {}
                  <p className={`mt-3 font-default text-sm leading-relaxed ${isFeatured ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {plan.tagline}
                  </p>

                  {}
                  <div className="mt-10 flex items-baseline gap-2">
                    <span className={`font-serif font-default text-5xl md:text-6xl leading-none tracking-tight ${isFeatured ? "text-primary-foreground" : "text-foreground"}`}>
                      ${price}
                    </span>
                    <span className={`font-mono text-xs uppercase tracking-wider ${isFeatured ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      /session
                    </span>
                  </div>
                  {billing === "annual" && <p className={`mt-2 font-mono text-[0.7rem] uppercase tracking-wider ${isFeatured ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      Package rate
                    </p>}

                  <Separator className={`my-8 ${isFeatured ? "bg-primary-foreground/20" : "bg-border"}`} />

                  {}
                  <ul className="flex flex-col gap-4 flex-1">
                    {plan.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3">
                        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-lg ${isFeatured ? "bg-primary-foreground/15" : "bg-primary"}`}>
                          <Check className={`h-3 w-3 ${isFeatured ? "text-primary-foreground" : "text-primary-foreground"}`} strokeWidth={3} />
                        </span>
                        <span className={`font-default text-sm leading-relaxed ${isFeatured ? "text-primary-foreground/90" : "text-foreground"}`}>
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  {}
                  <div className="mt-10">
                    <Link to="/GetInTouchPage" className="block">
                      <Button size="lg" className={`w-full h-12 font-mono text-xs uppercase tracking-widest rounded-lg ${isFeatured ? "bg-background text-foreground hover:bg-background/90" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                        {plan.cta}
                      </Button>
                    </Link>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {}
        <p className="mt-12 md:mt-16 text-center font-default text-sm text-muted-foreground">
          All bookings include a complimentary consultation. Inquire for availability.
        </p>
      </div>
    </section>;
}